import React, { useEffect, useState } from "react";
import api from "../API/axios";
import { Grid, TextField, Button, MenuItem, Box } from "@mui/material";
import styles from "./ContactDetails.module.css";
import Logo from "../components/Logo";
import SideBackground from "../components/SideBackground";
import NotificationSnackbar from "../components/NotificationSnackbar";
import useNotification from "../hooks/useNotification";
import { countries } from "../utils/country";

export default function ContactDetails() {
  const { notification, showNotification, hideNotification } =
    useNotification();
  const [form, setForm] = useState({
    address: "",
    address2: "",
    townCity: "",
    countryState: "",
    postCode: "",
    country: "GB",
    telephone: "",
  });

  const [phoneCode, setPhoneCode] = useState(
    countries.find((c) => c.code === form.country)?.phoneCode || "+44"
  );

  useEffect(() => {
    const selected = countries.find((c) => c.code === form.country);
    setPhoneCode(selected ? selected.phoneCode : "+44");
  }, [form.country]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleDetailsChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Get verification token from URL (e.g., ?token=...)
    const params = new URLSearchParams(window.location.search);
    const verificationToken = params.get("token");
    if (!verificationToken) {
      showNotification("Verification token missing.", "error");
      return;
    }
    try {
      await api.post("/Users/update-contact-details", {
        verificationToken,
        address: form.address,
        address2: form.address2,
        townCity: form.townCity,
        countryState: form.countryState,
        postCode: form.postCode,
        country: form.country,
        telephone: phoneCode + form.telephone,
      });
      // Redirect to Password page with the same verification token
      window.location.href = `/password?token=${verificationToken}`;
    } catch (err) {
      console.error(err);
      showNotification("Failed to submit contact details", "error");
    }
  };

  return (
    <div className={styles.contactDetailsRoot}>
      <div className={styles.contactDetailsLeft}>
        <div className={styles.contactDetailsLogoWrap}>
          <Logo />
        </div>
        <div className={styles.contactDetailsForm}>
          <h2>Complete Setup</h2>
          <form
            onSubmit={handleSubmit}
            style={{
              width: "100%",
              maxWidth: "536px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
              marginLeft: "auto",
            }}
          >
            <div
              style={{ display: "flex", justifyContent: "center", gap: "16px" }}
            >
              <TextField
                type="text"
                label="Address"
                name="address"
                value={form.address}
                onChange={handleChange}
                required
                autoFocus
                inputProps={{ maxLength: 100 }}
                sx={{
                  width: "100%",
                  maxWidth: 260,
                  marginBottom: 1,
                }}
              />
              <TextField
                type="text"
                label="Address 2"
                name="address2"
                value={form.address2}
                onChange={handleChange}
                inputProps={{ maxLength: 100 }}
                sx={{
                  width: "100%",
                  maxWidth: 260,
                  marginBottom: 1,
                }}
              />
            </div>

            <div
              style={{ display: "flex", justifyContent: "center", gap: "16px" }}
            >
              <TextField
                type="text"
                label="Town/City"
                name="townCity"
                value={form.townCity}
                onChange={handleChange}
                required
                inputProps={{ maxLength: 100 }}
                sx={{
                  width: "100%",
                  maxWidth: 260,
                  marginBottom: 1,
                }}
              />
              <TextField
                type="text"
                label="County/State"
                name="countryState"
                value={form.countryState}
                onChange={handleChange}
                inputProps={{ maxLength: 100 }}
                sx={{
                  width: "100%",
                  maxWidth: 260,
                  marginBottom: 1,
                }}
              />
            </div>

            <div
              style={{ display: "flex", justifyContent: "center", gap: "16px" }}
            >
              <TextField
                type="text"
                label="PostCode/ZipCode"
                name="postCode"
                value={form.postCode}
                onChange={(e) =>
                  handleDetailsChange("postCode", e.target.value)
                }
                required
                inputProps={{ maxLength: 10 }}
                sx={{
                  width: "100%",
                  maxWidth: 260,
                  marginBottom: 1,
                }}
              />

              <TextField
                fullWidth
                label="Telephone"
                name="telephone"
                required
                value={form.telephone}
                onChange={(e) =>
                  handleDetailsChange(
                    "telephone",
                    e.target.value.replace(/[^0-9]/g, "")
                  )
                }
                sx={{ width: 550, borderRadius: 2, background: "#fff" }}
                inputProps={{ maxLength: 15 }}
                InputProps={{
                  startAdornment: (
                    <Box
                      sx={{
                        minWidth: 64,
                        mr: 1,
                        pr: 1,
                        borderRight: "2px solid #E9E9E9",
                      }}
                    >
                      {phoneCode}
                    </Box>
                  ),
                }}
              />
            </div>

            <TextField
              select
              label="Country"
              name="country"
              value={form.country}
              onChange={handleChange}
              fullWidth
              required
              sx={{
                width: "100%",
                maxWidth: 260,
                marginBottom: 1,
                maxHeight: 400,
              }}
              SelectProps={{
                MenuProps: {
                  PaperProps: {
                    style: {
                      maxHeight: 220, // Set your desired max height here
                    },
                  },
                },
              }}
            >
              {countries.map((option) => (
                <MenuItem key={option.code} value={option.code}>
                  {option.name}
                </MenuItem>
              ))}
            </TextField>
            <div
              style={{ display: "flex", justifyContent: "center", gap: "16px" }}
            >
              <Button
                type="submit"
                variant="contained"
                size="large"
                sx={{
                  maxWidth: 260,
                  width: "100%",
                  backgroundColor: "var(--color-primary)",
                  "&:hover": { backgroundColor: "var(--color-secondary)" },
                }}
              >
                Continue
              </Button>
            </div>
          </form>
        </div>
      </div>
      <SideBackground>
        <div className={styles.contactDetailsBgOverlay}></div>
      </SideBackground>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={hideNotification}
      />
    </div>
  );
}
